// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StaticListProductListLine from "@monsma/Overrides/Widgets/StaticList/StaticListProductListLine";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const enum fields {
    showOverlayImageBadge = "showOverlayImageBadge",
    showOverlayTextBadge = "showOverlayTextBadge",
    maximumNumberOfOverlayBadges = "maximumNumberOfOverlayBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOverlayImageBadge]: boolean;
        [fields.showOverlayTextBadge]: boolean;
        [fields.maximumNumberOfOverlayBadges]: number;
    };
}
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState) => ({
    wishListState: getWishListState(state, state.pages.staticList.wishListId),
    productInfosByWishListLineId: state.pages.staticList.productInfosByWishListLineId,
});

// XNG-Changes
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface StaticListProductListStyles {
    mainWrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    linesWrapper?: InjectableCss;
}

export const staticListProductListStyles: StaticListProductListStyles = {
    mainWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        weight: 600,
    },
    linesWrapper: {
        css: css`
            margin-top: 10px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
};

const styles = staticListProductListStyles;

const StaticListProductList = ({
    wishListState,
    productInfosByWishListLineId,
    // XNG-Changes
    fields: { showOverlayImageBadge, showOverlayTextBadge, maximumNumberOfOverlayBadges },
}: Props) => {
    if (wishListState.isLoading || !wishListState.value || Object.keys(productInfosByWishListLineId).length === 0) {
        return null;
    }

    const wishList = wishListState.value;

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Typography {...styles.countText}>
                {wishList.wishListLinesCount === 1 && translate("{0} Product", wishList.wishListLinesCount.toString())}
                {wishList.wishListLinesCount > 1 && translate("{0} Products", wishList.wishListLinesCount.toString())}
            </Typography>
            <StyledWrapper {...styles.linesWrapper}>
                {wishList.wishListLineCollection?.map(wishListLine => {
                    const productInfo = productInfosByWishListLineId[wishListLine.id];
                    if (!productInfo) {
                        return null;
                    }

                    return (
                        <StaticListProductListLine
                            key={wishListLine.id}
                            wishListLine={wishListLine}
                            productInfo={productInfo}
                            // XNG-Changes
                            showOverlayImageBadge={showOverlayImageBadge}
                            showOverlayTextBadge={showOverlayTextBadge}
                            maximumNumberOfOverlayBadges={maximumNumberOfOverlayBadges}
                        />
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(StaticListProductList),
    definition: {
        group: "Static List",
        displayName: "Product List",
        allowedContexts: ["StaticListPage"],
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.showOverlayImageBadge,
                displayName: "Show Overlay Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.showOverlayTextBadge,
                displayName: "Show Overlay Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfOverlayBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showOverlayImageBadge || widget.fields.showOverlayTextBadge,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                ],
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
